import { Article, FeatureArticle } from '../../Elements/Articles';
import {
  FeatureArticleWrapper,
  PaginatorWrapper,
  TextInputWrapper,
  articleGrid,
  breadcrumbWrapper,
  clearLink,
  filterResultsContainer,
  filterResultsRow,
  galleryCSS,
  noResultDescription,
  noResultImage,
  rightAlign,
  searchText,
} from './styles/SectionFilterableArticle.styles';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import {
  BreadcrumbItems,
  CustomWindow,
  IArticle,
  ObjectEventType,
  SectionFilterableArticleProps,
  SeoDataTypes,
  TagTopic,
} from './types/SectionFilterableArticles.types';
import React, { useEffect, useRef, useState } from 'react';

import Block from '@vfuk/core-block';
import Heading from '@vfuk/core-heading';
import { Helmet } from 'react-helmet';
import Link from '@vfuk/core-link';
import Paginator from '@vfuk/core-paginator';
import SWBreadcrumb from '../SWBreadcrumb/SWBreadcrumb';
import SelectInputWithLabel from '@vfuk/core-select-input-with-label';
import SimpleGrid from '@vfuk/core-simple-grid';
import { StructuredData } from '../../../SEO/StructuredData';
import TextInputWithLabel from '@vfuk/core-text-input-with-label';
import { css } from 'styled-components';
import setupBreadcrumbAnalitycsTags from '../../../../helpers/setupBreadcrumbs';
import useSiteMetadata from '../../../../hooks/useSiteMetadata';
import { withPrefix } from 'gatsby';

// The size is overwritten from the contentful.
let PAGE_SIZE_FIRST = 10;
let PAGE_SIZE = 12;

declare const window: CustomWindow;

const SectionFilterableArticles = ({
  section,
  location,
}: SectionFilterableArticleProps) => {
  const {
    slug: articleIndexSlug,
    name,
    seoTitle,
    seoDescription,
    articleIndex,
    homePageName,
    homePageUrl,
    externalPageName,
    externalPageUrl,
    uiTextSearchDefault,
    uiTextCategoriesDropdownDefault,
    uiTextTopicsDropdownDefault,
    uiTextResultsText,
    uiTextBackToTopLabel,
    uiTextPaginationSeparator,
    uiTextNoResults,
    uiTextNoResultsDescription,
    uiImageNoResultsFound,
    uiLinkClear,
    tagCategories,
    tagTopics,
    filterSeoEventData,
    booleanApplySeoTrackingForFilter,
    pageSizeFirst,
    pageSize,
  } = section;

  const { siteUrl } = useSiteMetadata();

  PAGE_SIZE_FIRST = pageSizeFirst ? Number(pageSizeFirst) : PAGE_SIZE_FIRST;
  PAGE_SIZE = pageSize ? Number(pageSize) : PAGE_SIZE;
  const paramsObj = new URLSearchParams(location.search);
  // NB - queryCategoryString will be either a string value or empty value.
  // It will correspond to the value part of the dropdown objects.
  const queryCategoryString = paramsObj.get('query_category')
    ? decodeURIComponent(paramsObj.get('query_category'))
    : '';
  // The selected category dropdown label value.
  const categoryDisplayName = getTagNameFromValue(
    tagCategories,
    queryCategoryString
  );

  const queryTopicString = paramsObj.get('query_topic')
    ? decodeURIComponent(paramsObj.get('query_topic'))
    : '';
  const topicDisplayName = getTagNameFromValue(tagTopics, queryTopicString);

  const queryPageString = paramsObj.get('query_page')
    ? decodeURIComponent(paramsObj.get('query_page'))
    : '1';

  const queryCategoryObj = queryCategoryString
    ? { text: categoryDisplayName, value: queryCategoryString }
    : { text: uiTextCategoriesDropdownDefault, value: '', topics: [] };

  const queryTopicObj = queryTopicString
    ? { text: topicDisplayName, value: queryTopicString }
    : { text: uiTextTopicsDropdownDefault, value: '' };

  // Form category dropdown options.
  const categoryOptions = useRef(
    (tagCategories || []).map(tagCategory => ({
      text: tagCategory.displayName,
      value: tagCategory.slugName,
    }))
  );

  if (categoryOptions.current[0].text !== uiTextCategoriesDropdownDefault) {
    categoryOptions.current.unshift({
      text: uiTextCategoriesDropdownDefault,
      value: '',
      topics: [],
    });
  }

  // Setting up breadcrumbs analytics
  useEffect(() => {
    setupBreadcrumbAnalitycsTags();
  }, []);

  // Form topic dropdown options.
  const rawTopicOptions = (tagTopics || []).map(
    (tagTopic): TagTopic => ({
      text: tagTopic.displayName,
      value: tagTopic.slugName,
    })
  );
  rawTopicOptions.unshift({ text: uiTextTopicsDropdownDefault, value: '' });
  const [topicOptions, setTopicOptions] = useState(rawTopicOptions);

  const fullTopicOptions = useRef([]);
  // The currently selected category and topic dropdown values.

  const hasHydrated = useRef(false);

  const [breadcrumbItems, setBreadcrumbItems] = useState<BreadcrumbItems>({
    renderItems: [],
    seoItems: [],
  });

  const [seoData, setSeoData] = useState({
    event_type: 'category',
    filter_query: queryCategoryString,
  });

  const [filterList, setFilterList] = useState({
    category: categoryDisplayName,
    categoryValue: queryCategoryString,
    topic: topicDisplayName,
    topicValue: queryTopicString,
  });

  const [page, setPage] = useState(
    queryPageString ? Number(queryPageString) - 1 : 0
  );
  const firstItemPos =
    page > 0 ? page * PAGE_SIZE - (PAGE_SIZE - PAGE_SIZE_FIRST) : 0;

  const [searchQuery, setSearchQuery] = useState('');
  // Note that the category/topic dropdowns are controlled components.
  const [queryCategorySelected, setQueryCategorySelected] =
    useState(queryCategoryObj);
  const [queryTopicSelected, setQueryTopicSelected] = useState(queryTopicObj);
  const [{ content: contentData }] = articleIndex;
  const [{ content }] = contentData;
  const [ContentfulSectionFilterableArticleIndex] = content;
  // The unfiltered articles. Note that the articles are rendered on hydration, so
  // articles is populated on the useEffect(() => {}, []).
  const articles = useRef([]);
  // The filtered article list (i.e. after the search query and category/topic
  // filters are applied.
  const filteredArticles = formFilteredContent(
    articles.current,
    filterList,
    searchQuery
  );
  // Paged version of filteredArticles.
  const [pagedArticles, setPagedArticles] = useState(
    formPagedContent(filteredArticles)
  );
  // The article list as a view.
  const currentPageContent = pagedArticles[page] || [];

  const list = currentPageContent.map((article: IArticle) => {
    const isFeatured = article.featured;

    if (isFeatured) {
      return (
        <FeatureArticleWrapper
          className="featured-article-grid"
          key={article.id}
        >
          <FeatureArticle
            key={article.slug}
            indexSlug={articleIndexSlug}
            backgroundColor={'white'}
            data={article}
            seoTitle={seoTitle}
            appliedFilters={seoData}
          />
        </FeatureArticleWrapper>
      );
    } else {
      return (
        <Article
          key={article.slug}
          indexSlug={articleIndexSlug}
          backgroundColor={'white'}
          data={article}
          seoTitle={seoTitle}
          appliedFilters={seoData}
        />
      );
    }
  });

  const uiTextResultsTextRendered = formPageSummary(
    uiTextResultsText,
    pagedArticles.length > 1,
    firstItemPos,
    currentPageContent.length,
    filteredArticles.length
  );

  const [displayClearLink, setDisplayClearLink] = useState(false);

  function onFilterChange() {
    const currentPage = page + 1;
    let newUrl = location.pathname,
      searchUrl = '';

    if (filterList.categoryValue !== '') {
      searchUrl +=
        '&query_category=' + encodeURIComponent(filterList.categoryValue);
    }

    if (filterList.topicValue !== '') {
      searchUrl += '&query_topic=' + encodeURIComponent(filterList.topicValue);
    }

    if (currentPage !== 1) {
      searchUrl += '&query_page=' + currentPage;
    }

    if (searchUrl !== '') {
      searchUrl = '?' + searchUrl.substring(1, searchUrl.length);
    }

    newUrl += searchUrl;

    let oldSearchUrl = '';
    oldSearchUrl = window.location.href.split(
      window.location.origin + window.location.pathname
    )[1];

    if (window.history.state?.data === newUrl || searchUrl === oldSearchUrl)
      return;
    window.history.pushState(
      { action: 'filterChange', data: newUrl },
      '',
      newUrl
    );
  }

  useEffect(() => {
    fireSearchEvent();
  }, [searchQuery]);

  useEffect(() => {
    onFilterChange();
  }, [filterList]);

  useEffect(() => {
    if (
      categoryDisplayName === filterList.category &&
      topicDisplayName === filterList.topic &&
      Number(queryPageString) === page
    )
      return;

    const newFilter = {
      ...filterList,
      category: categoryDisplayName,
      categoryValue: queryCategoryString,
      topic: topicDisplayName,
      topicValue: queryTopicString,
      page: queryPageString ? Number(queryPageString) - 1 : 0,
    };
    let newQueryCategorySelected = categoryOptions.current[0];
    categoryOptions.current.some(category => {
      const isFound = categoryDisplayName === category.displayName;
      if (isFound) {
        newQueryCategorySelected = category;
      }
      return isFound;
    });

    let newQueryTopicSelected = topicOptions[0];
    topicOptions.some(topic => {
      const isFound = topicDisplayName === topic.text;
      if (isFound) {
        newQueryTopicSelected = topic;
      }
      return isFound;
    });

    if (queryCategoryString !== filterList.categoryValue) {
      setQueryCategorySelected(newQueryCategorySelected);
    }

    if (queryTopicString !== filterList.topicValue) {
      setQueryTopicSelected(newQueryTopicSelected);
    }

    if (Number(queryPageString) !== page) {
      setPage(newFilter.page);
    }

    setFilterList(newFilter);
  }, [location.search]);

  // Update the filterList from the Category dropdown field
  function onCategoryFilterChange(
    e: React.ChangeEvent<HTMLSelectElement> | ObjectEventType
  ) {
    setDisplayClearLink(true);
    // Handling default component event
    const firstOption = categoryOptions.current[0];
    const target = e.target ? e.target : e;
    const selectedCategory =
      target === undefined
        ? firstOption
        : categoryOptions.current.find(
            category => category.value === target.value
          );
    let newFilterList = {
      ...filterList,
      category: selectedCategory?.text || '',
      categoryValue: selectedCategory?.value || '',
    };
    let newQueryCategorySelected = {
      value: '',
    };
    let newTopicsSelected = [];

    // Handling select event
    const categoryFound = categoryOptions.current.find(
      category => target?.value === category.value
    );
    if (!categoryFound) {
      setQueryCategorySelected(uiTextCategoriesDropdownDefault);
      newFilterList = {
        ...filterList,
        category: '',
        categoryValue: '',
        page: 0,
      };
    } else {
      newQueryCategorySelected = categoryFound;
      newTopicsSelected = categoryFound.topics;
    }

    if (newQueryCategorySelected.value === '') {
      // The user has selected the first (null) option, so show all topics
      const newTopics = fullTopicOptions.current;
      setTopicOptions(newTopics);
    } else {
      // The user has selected a non-null option, so show the related topics
      const newTopics = newTopicsSelected;
      const hasSelectedTopic = newTopics.some(
        topic => filterList.topicValue === topic.value
      );
      if (!hasSelectedTopic) {
        newFilterList = {
          ...newFilterList,
          topic: uiTextTopicsDropdownDefault,
          topicValue: '',
        };
        setQueryTopicSelected(newTopics[0]);
      }
      setTopicOptions(newTopics);
    }

    setSeoData({
      event_type: 'category',
      filter_query: newFilterList.category,
    });
    setQueryCategorySelected(newQueryCategorySelected.value);
    setFilterList(newFilterList);
    setPage(0);
  }

  // Update the filterList from the Topic dropdown field
  function onTopicFilterChange(e: React.ChangeEvent<HTMLSelectElement>) {
    setDisplayClearLink(true);

    const firstTopicOption = topicOptions[0].text;
    const selectedTopic =
      e.target === undefined
        ? firstTopicOption
        : topicOptions.find(topic => topic.value === e.target.value);

    let newFilterList = {
      ...filterList,
      topic: selectedTopic?.text || '',
      topicValue: selectedTopic?.value || '',
      page: 0,
    };
    // If the category is not selected, select the first category containing the
    // new topic (but only if the topic is not the default).
    if (filterList.category === '' && e.value !== '') {
      const newCategory = categoryOptions.current.find(category =>
        category.topics.find(topic => topic.value === e.value)
      );
      if (newCategory) {
        // We found the corresponding category, so set it as the new category.
        newFilterList = { ...newFilterList, category: newCategory.value };
        setTopicOptions(newCategory.topics);
        setQueryCategorySelected(newCategory);
      } else {
        // The topic appears in no category, so set the category to the default (null).
        newFilterList = { ...newFilterList, category: '' };
        setTopicOptions(fullTopicOptions.current);
      }
    }
    let newQueryTopicSelected = { text: '', value: '' };
    topicOptions.some(topic => {
      const isFound = e.target?.value === topic.value;
      if (isFound) {
        newQueryTopicSelected = topic;
      }
      return isFound;
    });
    setQueryTopicSelected(newQueryTopicSelected.value);
    setSeoData({
      event_type: 'topic',
      filter_query: newFilterList.topic,
    });
    setFilterList(newFilterList);
    setPage(0);
  }

  useEffect(() => {
    const newFilter = { ...filterList, page: page };
    setFilterList(newFilter);
  }, [page]);

  const firePaginatorClickEvent = (targetValue: number) => {
    try {
      // fire the utag link event
      if (window.onPaginatorClickEvent) {
        window.onPaginatorClickEvent(targetValue);
      }
    } catch (e) {
      console.log(
        'onPaginatorClickEvent failed - please check the Page Changing Arrows Script'
      );
    }
  };

  const changePage = newPage => {
    firePaginatorClickEvent(newPage);
    setPage(newPage - 1);
    window.scrollTo(0, 0);
  };

  const runFilterEventData = (sData: SeoDataTypes) => {
    const filterEventData = filterSeoEventData.filterSeoEventData;

    if (window.onFilterUserEvent && filterEventData) {
      try {
        const payload = JSON.parse(filterEventData);
        const { type, data } = payload;

        // Fire the filterUserEvent
        window.onFilterUserEvent(type, { ...data, ...sData });
      } catch (e) {
        console.log(
          'onFilterUserEvent failed - please check the Filter SEO script JSON and that a SeoInteractionScript is correctly set up.'
        );
      }
    }
  };

  const fireSearchEvent = () => {
    if (searchQuery && !booleanApplySeoTrackingForFilter) {
      if (
        window.utag &&
        window.utag.link &&
        typeof window.utag.link === 'function'
      ) {
        window.utag.link({
          search_terms: 'Portal:SoHo:VHub:' + searchQuery,
          search_category: 'VHub',
          search_results: filteredArticles.length,
          tealium_event: 'search_event',
        });
      }
    }
  };

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDisplayClearLink(true);

    setSeoData({
      event_type: 'search',
      search_terms: e.target.value,
    });
    setSearchQuery(e.target.value);
    setPage(0);
  };

  useEffect(() => {
    const results = formFilteredContent(
      articles.current,
      filterList,
      searchQuery
    );

    setPagedArticles(formPagedContent(results));

    // SEO Tracking is allowed for the opco.
    if (booleanApplySeoTrackingForFilter && seoData.event_type !== 'clear') {
      runFilterEventData({
        ...seoData,
        search_results_count: results.length,
      });
    }
  }, [searchQuery, filterList, seoData]);

  useEffect(() => {
    // Effect helpers
    function makeTopics(topics = [], uiTextTopicsDropdownDefault) {
      const theTopics = topics.map(
        (topic: {
          name?: string;
          slugName?: string;
          displayName?: string;
        }) => ({
          text: topic.displayName || topic.name,
          value: topic.slugName,
        })
      );
      // Add default
      theTopics.unshift({
        text: uiTextTopicsDropdownDefault,
        value: '',
      });
      return theTopics;
    }

    // Add the articles.
    articles.current = ContentfulSectionFilterableArticleIndex.articles;

    // Form category dropdown options.
    categoryOptions.current = (tagCategories || []).map(tagCategory => ({
      text: tagCategory.displayName,
      value: tagCategory.slugName,
      topics: makeTopics(tagCategory.topics, uiTextTopicsDropdownDefault),
    }));
    /*
    categoryOptions.current.unshift({
      label: uiTextCategoriesDropdownDefault,
      value: null,
      topics: [],
    });
    */
    // form full topic options
    fullTopicOptions.current = (tagTopics || []).map(tagTopic => ({
      text: tagTopic.displayName,
      value: tagTopic.slugName,
    }));
    fullTopicOptions.current.unshift({
      text: uiTextTopicsDropdownDefault,
      value: '',
    });
    // On initial page load, set the default category/topics by simulating a
    // user generated change event matching the initial conditions.
    const e = { text: categoryDisplayName, value: queryCategoryString };
    onCategoryFilterChange(e);
  }, []);

  // Remove the visually hidden h2 Element, embedded in the Breadcrumb component
  useEffect(() => {
    const elems = Array.from(document.getElementsByTagName('h2'));
    elems.forEach(elem => {
      if (elem.className === 'visually-hidden') elem.remove();
    });
  });
  const clearFilterState = () => {
    const initialQueryCategoryObj = {
      text: uiTextCategoriesDropdownDefault,
      value: '',
    };

    setQueryCategorySelected(initialQueryCategoryObj);

    const initialQueryTopicObj = {
      text: uiTextTopicsDropdownDefault,
      value: '',
    };

    setQueryTopicSelected(initialQueryTopicObj);
    setTopicOptions(rawTopicOptions);
    setSearchQuery('');

    setFilterList({
      category: '',
      categoryValue: '',
      topic: '',
      topicValue: '',
    });

    setSeoData({
      event_type: 'clear',
      search_terms: '',
    });

    setSeoData({
      event_type: 'clear',
      search_terms: '',
    });

    setSeoData({
      event_type: 'clear',
      search_terms: '',
    });

    setPage(0);
    setDisplayClearLink(false);
  };

  // Set flag to true, when the DOM has re-rendered at least once
  useEffect(() => {
    hasHydrated.current = true;
  }, []);

  const DisplayListOfArticles = ({ list }) => {
    if (hasHydrated.current && list.length === 0) {
      return (
        <>
          {uiImageNoResultsFound && (
            <GatsbyImage
              image={getImage(uiImageNoResultsFound)}
              css={noResultImage}
              alt=""
            />
          )}
          <Heading level={5} justify="center">
            <b>{uiTextNoResults}</b>
          </Heading>
          <p css={noResultDescription}>{uiTextNoResultsDescription}</p>
        </>
      );
    }

    return <div css={articleGrid}>{list}</div>;
  };

  // and on empty search & filter fields
  useEffect(() => {
    if (
      filterList.category !== '' ||
      filterList.topic !== '' ||
      searchQuery !== ''
    ) {
      setDisplayClearLink(true);
    } else {
      setDisplayClearLink(false);
    }
  }, []);

  // Display the 'Clear' link as long as the
  // search & filter fields are not empty
  useEffect(() => {
    if (
      filterList.category !== '' ||
      filterList.topic !== '' ||
      searchQuery !== ''
    ) {
      setDisplayClearLink(true);
    } else {
      setDisplayClearLink(false);
    }
  }, [filterList, searchQuery]);

  // Add smooth scrolling to the 'Back to top' link
  const handleSmoothScroll = () => {
    const target = document.querySelector('#scroll-root');
    target?.scrollIntoView({ behavior: 'smooth' });
  };

  const runBreadcrumbEventData = async sData => {
    if (window.onBreadcrumbsUserEvent && sData) {
      try {
        const finished = await window.onBreadcrumbsUserEvent(
          'sectionFilterableArticles',
          {
            ...sData,
          }
        );
      } catch (e) {
        console.log(
          'onBreadcrumbsUserEvent failed - please check the Navigation SEO script JSON and that a SeoInteractionScript is correctly set up.'
        );
      }
    }
  };

  const onClickHandler = async (e, href) => {
    e.preventDefault();
    await runBreadcrumbEventData({ location: 'sectionFilterableArticles' });
    window.location.href = href;
  };

  useEffect(() => {
    setBreadcrumbItems(createBreadCrumbItems());
  }, [
    externalPageName,
    externalPageUrl,
    homePageName,
    name,
    siteUrl,
    location.pathname,
  ]);

  const createBreadCrumbItems = () => {
    const items: BreadcrumbItems = { renderItems: [], seoItems: [] };

    if (externalPageName && externalPageUrl) {
      items.renderItems.push({
        id: 'item-1',
        text: externalPageName,
        href: externalPageUrl,
      });
      items.seoItems.push({
        '@type': 'ListItem',
        position: 1,
        name: externalPageName,
        item: externalPageUrl,
      });
    }

    items.renderItems.push(
      {
        id: 'item-' + (items.renderItems.length + 1),
        text: homePageName,
        href: withPrefix(homePageUrl),
      },
      {
        id: 'item-' + (items.renderItems.length + 2),
        text: name,
      }
    );

    items.seoItems.push(
      {
        '@type': 'ListItem',
        position: items.seoItems.length + 1,
        name: homePageName,
        item: `${siteUrl}${withPrefix(homePageUrl)}`
      },
      {
        '@type': 'ListItem',
        position: items.seoItems.length + 2,
        name: name,
        item: `${siteUrl}${location.pathname}`,
      }
    );
    return items;
  };

  return (
    <>
      <Helmet>
        {seoTitle.trim().length > 0 && <title>{seoTitle}</title>}
        {seoDescription.trim().length > 0 && (
          <meta name="description" content={seoDescription} />
        )}
      </Helmet>
      <StructuredData>
        {{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: breadcrumbItems.seoItems,
        }}
      </StructuredData>
      <div data-testid="breadCrumb-knowledgeCenter" css={breadcrumbWrapper}>
        <SWBreadcrumb
          location="sectionFilterableArticles"
          items={
            breadcrumbItems.renderItems.length > 0
              ? breadcrumbItems.renderItems
              : createBreadCrumbItems().renderItems
          }
        />
      </div>
      <Block css={galleryCSS}>
        {/* Filter drop-downs */}
        <div className="spring">
          <SimpleGrid
            layout={{
              lg: [6, 3, 3, 12],
              md: [12, 6, 6, 12],
              sm: [12, 12, 12],
            }}
            justify="right"
            spacing={4}
          >
            <TextInputWrapper>
              <TextInputWithLabel
                fieldWrapper={{
                  label: '',
                  showLabel: false,
                  width: 'full',
                }}
                textInput={{
                  type: 'text',
                  id: 'searchInput',
                  placeholder: uiTextSearchDefault,
                  value: searchQuery,
                  onChange: e => onSearch(e),
                }}
              />
            </TextInputWrapper>
            <SelectInputWithLabel
              fieldWrapper={{
                label: 'Default SelectInputWithLabel',
                showLabel: false,
                width: 'full',
              }}
              selectInput={{
                id: 'default-example',
                name: 'defaultName',
                value: queryCategorySelected,
                onChange: event => onCategoryFilterChange(event),
                options: categoryOptions.current,
              }}
            />
            <SelectInputWithLabel
              fieldWrapper={{
                label: 'Default SelectInputWithLabel',
                showLabel: false,
                width: 'full',
              }}
              selectInput={{
                id: 'default-example-topic',
                name: 'defaultName-topic',
                value: queryTopicSelected,
                onChange: event => onTopicFilterChange(event),
                options: topicOptions,
              }}
            />

            <div css={filterResultsContainer}>
              <div css={filterResultsRow}>
                <div
                  style={{ display: displayClearLink ? 'block' : 'none' }}
                  css={clearLink}
                >
                  <Link onClick={clearFilterState} text={uiLinkClear} />
                </div>
                {list.length > 0 && (
                  <span css={searchText}>{uiTextResultsTextRendered}</span>
                )}
              </div>
            </div>
          </SimpleGrid>
        </div>
      </Block>

      <Block
        css={css`
          background-color: var(--gallery);
        `}
      >
        {/* Article index grid */}
        <div className="spring">
          <DisplayListOfArticles list={list} />
        </div>
        {pagedArticles.length > 1 && (
          <PaginatorWrapper data-testid="pagination" isSecondLanguageOn={homePageUrl !== '/' && homePageUrl !== null}>
            <Paginator
              id="the-paginator"
              numberOfPages={pagedArticles.length}
              currentPage={page + 1}
              onClick={changePage}
            />
          </PaginatorWrapper>
        )}
      </Block>

      <Block
        css={css`
          background-color: var(--gallery);
        `}
      >
        <div className="spring">
          <div css={rightAlign}>
            <Link onClick={handleSmoothScroll} text={uiTextBackToTopLabel} />
          </div>
        </div>
      </Block>
    </>
  );
};

function formFilteredContent(articles, filters, query) {
  const categoryFilter = filters.category;
  const topicFilter = filters.topic;

  const filteredData = articles.filter(article => {
    let hasCategoryMatch = false;
    let hasTopicMatch = false;
    let hasTitleMatch = false;
    let hasSummaryMatch = false;
    let hasKeywordMatch = false;

    if (filters.categoryValue !== '') {
      hasCategoryMatch = article.categoryTags.some(
        tag => (tag.displayName === categoryFilter) || (tag.name === categoryFilter)
      );
    } else {
      hasCategoryMatch = true;
    }

    if (filters.topicValue !== '') {
      hasTopicMatch = article.topicTags.some(
        tag => (tag.displayName === topicFilter) || (tag.name === topicFilter)
      );
    } else {
      hasTopicMatch = true;
    }

    if (query.length > 0) {
      hasTitleMatch = article.pageArticleName
        .toLowerCase()
        .includes(query.toLowerCase());

      hasSummaryMatch = article.summary
        .toLowerCase()
        .includes(query.toLowerCase());

      hasKeywordMatch = article.keywords
        .map(keyword => keyword.toLowerCase())
        .includes(query.toLowerCase());
    } else {
      hasTitleMatch = true;
      hasKeywordMatch = true;
    }

    return (
      hasCategoryMatch &&
      hasTopicMatch &&
      (hasTitleMatch || hasSummaryMatch || hasKeywordMatch)
    );
  });
  return filteredData;
}

function formPageSummary(
  templateString: string,
  hasMultiplePages: boolean,
  firstItemPos: number,
  currentPageContentLength: number,
  filteredArticlesLength: number
) {
  const resultsRange: string = hasMultiplePages
    ? `${firstItemPos + 1}-${currentPageContentLength + firstItemPos}`
    : String(filteredArticlesLength);

  if (filteredArticlesLength !== 0) {
    return templateString
      .replace('{resultsRange}', resultsRange)
      .replace('{totalResults}', String(filteredArticlesLength));
  }
  return '';
}

function formPagedContent(data) {
  let pagedData = data.reduce((resultArray, item, index) => {
    const isFirstPage = index < PAGE_SIZE_FIRST;
    const pageSize = isFirstPage ? PAGE_SIZE_FIRST : PAGE_SIZE;
    const pageIndex = isFirstPage
      ? Math.floor(index / pageSize)
      : Math.max(Math.floor((index - PAGE_SIZE_FIRST) / pageSize + 1), 1);

    if (!resultArray[pageIndex]) {
      // start new page
      resultArray[pageIndex] = [];
    }
    resultArray[pageIndex].push(item);
    return resultArray;
  }, []);
  if (pagedData.length === 0) {
    pagedData = [[]];
  }
  return pagedData;
}

function getTagValueFromName(tagList, tagName) {
  let tagValue = '';
  if (tagName !== '') {
    tagList.some(tag => {
      const isFound = tag.displayName === tagName;
      if (isFound) {
        tagValue = tag.slugName;
      }
      return isFound;
    });
  }
  return tagValue;
}

function getTagNameFromValue(tagList, tagValue) {
  let tagName = '';
  if (tagValue !== '') {
    tagList.some(tag => {
      const isFound = tag.slugName === tagValue;
      if (isFound) {
        tagName = tag.displayName;
      }
    });
  }
  return tagName;
}

export default SectionFilterableArticles;
